import { focusInput } from "../../shared/utils";
import { onCanvas } from "../../shared/mouseFunctions";
import AbstractShapeMode from "./AbstractShapeMode";
import Coordinate from "../support/Coordinate";
import Polygon from "../shapes/Polygon";
import CurveSegment from "../support/segments/CurveSegment";
import BezierSegment from "../support/segments/BezierSegment";
import { DRAW } from "../../tools/actions/ActionHelper";

export default class PolygonMode extends AbstractShapeMode {
    coordinates = [];
    segmentTypes = [];
    currentInput;

    display(g) {
        if (this.coordinates.length == 1) {
            this.coordinates[0].display(g);
        }
        if (this.coordinates.length > 0) {
            g.noFill()
            g.strokeJoin(g.ROUND);
            g.strokeCap(g.SQUARE);
            const appearance = this.drawMenu.appearance;
            if (appearance.strokeColor) {
                appearance.strokeColor.setAlpha(255);
                g.stroke(appearance.strokeColor);
            } else {
                g.stroke(0);
            }
            g.strokeWeight(appearance.scaledStrokeWidth);
            g.beginShape();
            this.coordinates.map((c) => g.vertex(c.currentX, c.currentY));
            if (onCanvas(this.provider)) {
                let newCoordinate = this.newCoordinate;
                g.vertex(newCoordinate.currentX, newCoordinate.currentY);
            }
            g.endShape();
        }
    }

    activate() {
        this.input.setDrawMode('polygon');
        this.drawMenu.enableFill();
        this.drawMenu.showSegmentTypeSelector();
        this.drawMenu.hideCanopyStyleSelector();
    }

    pressMouseOnCanvas() {
        let lastCoordinate = this.coordinates[this.coordinates.length - 1];
        if (lastCoordinate && lastCoordinate.hovered()) {
            return;
        }
        if (this.coordinates.length > 2 && this.coordinates[0].hovered()) {
            this.makeShape();
            return;
        }
        if (this.coordinates.length === 0) this.input.showDimensions();
        this.currentInput = this.input.multiInput.getInput(this.coordinates.length);
        this.currentInput.show();
        this.coordinates.push(this.newCoordinate);
        if (this.coordinates.length > 1) {
            this.segmentTypes.push(this.drawMenu.segmentType);
        }
        this.resetSticky();
    }

    makeShape() {
        if (this.coordinates.length < 3) {
            return;
        }
        let polygon = new Polygon(
            this.provider,
            {
                coordinates: this.buildCoordinateData(),
                layer: this.layer,
                addedDate: this.addedDate,
                removedDate: this.removedDate,
            },
            this.drawMenu.appearance.clone(),
            this.speciesIds,
            this.labels
        );
        this.actions.execute(DRAW, polygon);
        this.reset();
    }

    buildCoordinateData() {
        let coords = this.coordinates.map((c, i) => {
            if (i === 0) return [c.absX, c.absY];
            let type = this.segmentTypes[i - 1];
            let prev = this.coordinates[i - 1];
            if (type === 'curve') {
                let cp = CurveSegment.defaultControlPoint(prev, c);
                return [c.absX, c.absY, "curve", cp.x, cp.y];
            }
            if (type === 'bezier') {
                let cps = BezierSegment.defaultControlPoints(prev, c);
                return [c.absX, c.absY, "bezier", cps.cp1.x, cps.cp1.y, cps.cp2.x, cps.cp2.y];
            }
            return [c.absX, c.absY];
        });

        // Add closing segment data if the selected type is not straight
        let closingType = this.drawMenu.segmentType;
        let last = this.coordinates[this.coordinates.length - 1];
        let first = this.coordinates[0];
        if (closingType === 'curve') {
            let cp = CurveSegment.defaultControlPoint(last, first);
            coords.push(["closing", "curve", cp.x, cp.y]);
        } else if (closingType === 'bezier') {
            let cps = BezierSegment.defaultControlPoints(last, first);
            coords.push(["closing", "bezier", cps.cp1.x, cps.cp1.y, cps.cp2.x, cps.cp2.y]);
        }

        return coords;
    }

    mouseMoved() {
        if (this.currentInput && !this.sticky['segment']) {
            this.currentInput.value(
                this.coordinates[this.coordinates.length - 1].distanceTo(this.view.snappedCoordinate)
            );
        }
    }

    doubleClickedOnCanvas() {
        this.makeShape();
        return true;
    }

    reset() {
        super.reset();
        this.coordinates = [];
        this.segmentTypes = [];
        this.currentInput = null;
    }

    getTips() {
        if (this.touch) {
            if (this.coordinates.length == 0) {
                return 'Tap to place the first point of the polygon.';
            }
            return 'Tap to add points. Tap the first point to close and finish.';
        }
        if (this.coordinates.length == 0) {
            return 'Click to place the first point of the polygon.';
        }
        return 'Click to place the next point. Double click or press enter to finish. Press 0-9 to focus input.';
    }

    get newCoordinate() {
        if (this.sticky['segment']) {
            let length = this.sticky['segment'].value();
            let lastCoordinate = this.coordinates[this.coordinates.length - 1];
            let angle = lastCoordinate.angleTo(this.view.snappedCoordinate);
            let x = lastCoordinate.absX + length * this.p.cos(angle);
            let y = lastCoordinate.absY + length * this.p.sin(angle);
            return new Coordinate(this.provider, x, y);
        }
        return this.view.snappedCoordinate;
    }

    pressKey(code) {
        switch (code) {
            case 49: // 1
                focusInput(this.getInput(0));
                return;
            case 50: // 2
                focusInput(this.getInput(1));
                return;
            case 51: // 3
                focusInput(this.getInput(2));
                return;
            case 52: // 4
                focusInput(this.getInput(3));
                return;
            case 53: // 5
                focusInput(this.getInput(4));
                return;
            case 54: // 6
                focusInput(this.getInput(5));
                return;
            case 55: // 7
                focusInput(this.getInput(6));
                return;
            case 56: // 8
                focusInput(this.getInput(7));
                return;
            case 57: // 9
                focusInput(this.getInput(8));
                return;
        }
    }
}
